/**
 * LoggingErrorBoundary - React error boundary that logs render crashes
 */

import { Component, type ReactNode, type ErrorInfo } from 'react';
import { WebComponentCode } from './types';
import { WebLogger } from './WebLogger';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

export class LoggingErrorBoundary extends Component<Props, State> {
  private logger = new WebLogger(WebComponentCode.WEB_UI, 'ErrorBoundary');

  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    this.logger.error('Component render crash', error, {
      componentStack: errorInfo.componentStack || undefined,
    });
  }

  render(): ReactNode {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '200px',
            padding: '32px',
            fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
            color: '#991b1b',
          }}
        >
          <h2 style={{ margin: '0 0 8px', fontSize: '18px' }}>Something went wrong</h2>
          <p style={{ margin: '0 0 16px', color: '#666', fontSize: '14px' }}>
            {this.state.error?.message || 'An unexpected error occurred'}
          </p>
          <button
            onClick={() => {
              this.setState({ hasError: false, error: null });
              window.location.reload();
            }}
            style={{
              padding: '8px 20px',
              border: '1px solid #ccc',
              borderRadius: '6px',
              background: '#fff',
              cursor: 'pointer',
              fontSize: '14px',
            }}
          >
            Reload Page
          </button>
        </div>
      );
    }

    return this.props.children;
  }
}
